import { http, isMessageError } from '@firefly/fly-http';
import { WechatSDK } from './sdk';
import {
  PaymentAgent,
  PaymentExecutionOptions,
  PaymentExecutionState,
  PaymentMethod,
} from './typing';

export interface PaymentExecution {
  orderId: string;
  method: string;
  args: Record<string, string>;
}

type ExecuteHandler = (
  payment: PaymentExecution,
  options?: PaymentExecutionOptions,
) => Promise<PaymentExecutionState>;
const handlers: Record<PaymentMethod, ExecuteHandler> = {} as any;

export function openWindow(url: string): Promise<PaymentExecutionState> {
  window.open(url);
  return new Promise((resolve) => {
    resolve('confirm');
  });
}
export function navigateToURL(url: string): Promise<PaymentExecutionState> {
  window.location.href = url;
  return new Promise<PaymentExecutionState>((resolve) => {
    //可能导致跳转 后退 还在加载中
    resolve('pending');
  });
}

function openPaymentPage({
  orderId,
  method,
  args,
}: PaymentExecution): Promise<PaymentExecutionState> {
  const params = new URLSearchParams();
  params.set('method', method);
  if (args.returnUrl) {
    params.set('returnUrl', args.returnUrl);
  }
  const url = `/checkout/orders/${orderId}/execute.html?${params.toString()}`;
  return openWindow(url);
}
function resolveExecuteLink(
  orderId: string,
  params: URLSearchParams,
  {
    agent,
  }: {
    agent?: PaymentAgent;
  },
) {
  if (agent) {
    return `${
      agent.target
    }/checkout/orders/${orderId}/agent-execute.html?${params.toString()}`;
  }
  return `/checkout/orders/${orderId}/execute.html?${params.toString()}`;
}
function createRedirectPaymentPageHandler(method: string) {
  return function redirectPaymentPage(
    { orderId, args }: PaymentExecution,
    { agent }: PaymentExecutionOptions = {},
  ) {
    const params = new URLSearchParams();
    params.set('method', method);
    if (args.returnUrl) {
      params.set('returnUrl', args.returnUrl);
    }
    if (args.quitUrl) {
      params.set('quitUrl', args.quitUrl);
    }
    const link = resolveExecuteLink(orderId, params, { agent });

    return navigateToURL(link);
  };
}
export function requestPaymentExecute(
  orderId: string,
  method: string,
  execute: (payment: { request: any }) => Promise<PaymentExecutionState>,
): Promise<PaymentExecutionState> {
  return new Promise<PaymentExecutionState>((resolve, reject) => {
    http.post(`/api/checkout/orders/${orderId}/execute`, {
      method: method,
    }).then(
      (response) => {
        resolve(execute(response));
      },
      (reason) => {
        if (isMessageError(reason)) {
          const { errorInfo } = reason;
          if (errorInfo.code === 'PAY_ERR_ORDER_IS_COMPLETED') {
            resolve('completed');
            return;
          }
        }
        reject(reason);
      },
    );
  });
}

handlers['ALIPAY:PAGE'] = (execution: PaymentExecution) => {
  // const { method, args } = execution;
  // if (!args.returnUrl) {
  //   return Promise.reject(new Error(`method: ${method} returnUrl is required`));
  // }
  return openPaymentPage(execution);
};
handlers['ALIPAY:WAP'] = createRedirectPaymentPageHandler('ALIPAY:WAP');
handlers['WECHATPAY:H5'] = function redirectPaymentPage(
  { orderId, args }: PaymentExecution,
  { agent }: PaymentExecutionOptions = {},
) {
  //必须确认支付 用于微信支付模式
  // sessionStorage.setItem(NEED_CONFIRM_PAYMENT_SESSION_STORAGE_KEY, orderId);
  const params = new URLSearchParams();
  params.set('method', 'WECHATPAY:H5');
  if (args.returnUrl) {
    params.set('returnUrl', args.returnUrl);
  }
  if (args.quitUrl) {
    params.set('quitUrl', args.quitUrl);
  }

  const link = resolveExecuteLink(orderId, params, { agent });

  return new Promise<PaymentExecutionState>((resolve) => {
    //挂起
    resolve('suspend');
    setTimeout(() => {
      window.location.href = link;
    });
  });
};
handlers['WECHATPAY:JSAPI'] = ({ orderId }: PaymentExecution) => {
  return requestPaymentExecute(orderId, 'WECHATPAY:JSAPI', (payment) => {
    return new Promise<PaymentExecutionState>((resolve, reject) => {
      WechatSDK.executePay(payment.request)
        .then((result) => {
          if (result === 'ok') {
            resolve('completed');
          } else if (result === 'cancel') {
            reject('cancel');
          } else {
            reject(result);
          }
        })
        .catch((reason) => {
          reject(reason);
        });
    });
  });
};

export default function executePayment(
  orderId: string,
  method: PaymentMethod,
  args: Record<string, any>,
  options?: PaymentExecutionOptions,
): Promise<PaymentExecutionState> {
  const handler = handlers[method];
  if (handler) {
    return handler(
      {
        orderId: orderId,
        method: method,
        args: args,
      },
      options,
    );
  }
  return Promise.reject('不支持的支付方式：' + method);
}
